import {
  Avatar,
  Box,
  Skeleton,
  Stack,
  Typography,
  TypographyProps,
} from "@mui/material"
import {useQuery} from "@tanstack/react-query"
import {FC, Fragment, ReactNode, useMemo, useState} from "react"
import {decodeToken} from "react-jwt"
import {Mail} from "../../../assets"
import {ContactModal, NewButton, SupportModal} from "../../../components"
import {useConfigProvider} from "../../../config"
import {stringToAvatar} from "../../../helpers"
import useStorage, {StorageData} from "../../../hooks/useStorage"
import {accountEndpoints} from "../../../services"
import {Account, AccountTeam, Token} from "../../../types"
import {Trans, useTranslation} from "react-i18next"
import TruncatedText from "../../../components/molecules/TruncatedText/TruncatedText"
import {CrmAccount} from "../../../types/CrmAccount"

type DetailProps = {
  isLoading: boolean
  title: string
  detail?: ReactNode
  detailProps?: TypographyProps
  id: string
  truncated?: boolean
  width?: string
}

const Detail: FC<DetailProps> = (props) => {
  const {
    isLoading,
    title,
    detail,
    detailProps,
    id,
    truncated = false,
    width,
  } = props
  const {t} = useTranslation()
  return (
    <Stack
      component="article"
      direction="column"
      gap="5px"
      id={id}
      width={width || "124px"}
    >
      <Typography
        fontSize="16px"
        fontWeight="400"
        color="#224957"
        lineHeight="normal"
        whiteSpace="nowrap"
      >
        {isLoading ? (
          <Fragment>
            <Skeleton width="98px" variant="text" animation="wave" />
          </Fragment>
        ) : (
          t(title)
        )}
      </Typography>
      {truncated ? (
        <TruncatedText
          text={detail?.toString() || ""}
          isLoading={isLoading}
          tooltipProps={{
            sx: {
              "& .MuiTooltip-tooltip": {
                mt: "3px !important",
                textAlign: "left",
                borderRadius: 0,
                border: "1px solid #1B1B1B",
                bgcolor: "#FFF",
                color: "#242D35",
                p: "2px 8px",
              },
            },
          }}
          textSx={{
            fontSize: "14px",
            fontWeight: "400",
            color: "#595958",
            lineHeight: "normal",
          }}
        />
      ) : (
        <Typography
          fontSize="14px"
          fontWeight="400"
          color="#595958"
          lineHeight="normal"
          {...detailProps}
        >
          {isLoading ? (
            <Fragment>
              <Skeleton width="164px" variant="text" animation="wave" />
            </Fragment>
          ) : (
            detail
          )}
        </Typography>
      )}
    </Stack>
  )
}

type MemberProps = {
  name: string
  id?: string
  loading?: boolean
  onClick?: () => void
}

const Member: FC<MemberProps> = (props) => {
  const {name, id, loading, onClick} = props
  const {getProfilePicture, getJobTitle} = accountEndpoints()
  const {t} = useTranslation()

  const getProfilePictureQuery = useQuery({
    queryKey: ["profilePicture", id],
    queryFn: () => getProfilePicture(id!!, "64x64"),
    enabled: !!id,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  })

  const getJobTitleQuery = useQuery<{jobTitle: string}>({
    queryKey: ["jobTitle", id],
    queryFn: () => getJobTitle(id?.replace(".onmicrosoft", "") || ""),
    enabled: !!id,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  })

  const profilePicture = useMemo(() => {
    if (getProfilePictureQuery.data) {
      return URL.createObjectURL(getProfilePictureQuery.data)
    }
    return ""
  }, [getProfilePictureQuery.data])

  const isLoadingPicture =
    loading ||
    getProfilePictureQuery.isLoading ||
    getProfilePictureQuery.isFetching
  const isLoadingJobTitle =
    loading || getJobTitleQuery.isLoading || getJobTitleQuery.isFetching

  const jobTitle = getJobTitleQuery.data?.jobTitle

  return (
    <Stack
      component="article"
      direction="column"
      alignItems="center"
      justifyContent="flex-start"
      gap="8px"
    >
      {isLoadingPicture ? (
        <Skeleton animation="wave" variant="circular" width={64} height={64} />
      ) : (
        <Avatar
          {...stringToAvatar(name, {
            width: "64px",
            height: "64px",
            fontSize: "0.875rem",
          })}
          src={profilePicture || undefined}
          alt={name}
          slotProps={{
            img: {width: "64px", height: "64px", loading: "lazy"},
          }}
        />
      )}
      <Stack direction="column">
        <Typography
          fontSize="14px"
          lineHeight="1.43"
          textAlign="center"
          color="#1c1c1c"
          whiteSpace="nowrap"
          onClick={loading ? undefined : onClick}
          sx={{
            cursor: loading ? "default" : "pointer",
          }}
        >
          {loading ? (
            <Skeleton variant="text" width="98px" animation="wave" />
          ) : (
            <TruncatedText
              text={name}
              textSx={{fontFamily: "inherit", maxWidth: "120px"}}
              tooltipProps={{
                sx: {
                  "& .MuiTooltip-tooltip": {
                    mt: "3px !important",
                    textAlign: "left",
                    borderRadius: 0,
                    border: "1px solid #1B1B1B",
                    bgcolor: "#FFF",
                    color: "#242D35",
                    p: "2px 8px",
                  },
                },
              }}
            />
          )}
        </Typography>
        <Typography
          fontSize="12px"
          lineHeight="1.33"
          textAlign="center"
          color="#7a7a79"
          whiteSpace="nowrap"
        >
          {isLoadingJobTitle ? (
            <Skeleton variant="text" width="84px" animation="wave" />
          ) : (
            <TruncatedText
              text={jobTitle ? t(jobTitle) : ""}
              textSx={{fontFamily: "inherit", maxWidth: "120px"}}
              tooltipProps={{
                sx: {
                  "& .MuiTooltip-tooltip": {
                    mt: "3px !important",
                    textAlign: "left",
                    borderRadius: 0,
                    border: "1px solid #1B1B1B",
                    bgcolor: "#FFF",
                    color: "#242D35",
                    p: "2px 8px",
                  },
                },
              }}
            />
          )}
        </Typography>
      </Stack>
      {loading ? (
        <Skeleton
          variant="rounded"
          height="24px"
          width="24px"
          animation="wave"
        />
      ) : (
        <NewButton
          variant="text"
          color="secondary"
          size="medium"
          icon
          IconLeft={<Mail />}
          onClick={onClick}
          sx={{
            minWidth: 0,
            padding: 0,
            width: "24px",
            height: "24px",
          }}
        />
      )}
    </Stack>
  )
}

const Details: FC = () => {
  const {token} = useConfigProvider()
  const storage = useStorage("session", StorageData.crmAccount)
  const decoded = decodeToken<Token>(token)
  const accountNumber =
    decoded?.companyId === CrmAccount.Internal
      ? storage.get() || ""
      : decoded?.companyId || ""
  const {t} = useTranslation()

  const [selectedMember, setSelectedMember] = useState<AccountTeam | null>(null)
  const [showContactTeam, setShowContactTeam] = useState<boolean>(false)
  const [showSupportModal, setShowSupportModal] = useState<boolean>(false)

  const {getAccountTeamByAccountNumber, getAccountByAccountNumber} =
    accountEndpoints()

  const getAccounTeamQuery = useQuery<AccountTeam[]>({
    queryKey: ["accountTeam", accountNumber],
    queryFn: () => getAccountTeamByAccountNumber(accountNumber),
    enabled: !!accountNumber && accountNumber !== CrmAccount.Internal,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  })

  const getAccountQuery = useQuery<Account>({
    queryKey: ["account", accountNumber],
    queryFn: () => getAccountByAccountNumber(accountNumber),
    enabled: !!accountNumber && accountNumber !== CrmAccount.Internal,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  })

  const isAccountError =
    (getAccountQuery.isError ||
      getAccountQuery.isRefetchError ||
      getAccountQuery.isLoadingError ||
      !getAccountQuery.data) &&
    !(getAccountQuery.isLoading || getAccountQuery.isFetching) &&
    accountNumber !== CrmAccount.Internal

  const isAccountTeamError =
    (getAccounTeamQuery.isError ||
      getAccounTeamQuery.isRefetchError ||
      getAccounTeamQuery.isLoadingError ||
      !getAccounTeamQuery.data) &&
    !(getAccounTeamQuery.isLoading || getAccounTeamQuery.isFetching)
  const isLoadingAccount =
    (getAccountQuery.isLoading ||
      getAccountQuery.isFetching ||
      isAccountError) &&
    accountNumber !== CrmAccount.Internal

  const isLoadingAccountTeam =
    getAccounTeamQuery.isLoading ||
    getAccounTeamQuery.isFetching ||
    isAccountError ||
    isAccountTeamError

  const handleContactTeamToggle = (member: AccountTeam) => {
    setSelectedMember(member)
    setShowContactTeam(true)
  }

  const handleOpenSupportModal = () => {
    setShowSupportModal((open) => !open)
  }

  const handleRefetchAll = () => {
    getAccountQuery.refetch()
    getAccounTeamQuery.refetch()
  }

  const handleRefetchAccount = () => {
    getAccountQuery.refetch()
  }

  return (
    <Fragment>
      <Stack
        component="main"
        id="profile-details"
        aria-label="Profile Details"
        direction="row"
        height="100%"
        paddingX="32px"
        paddingY="24px"
      >
        <Stack
          component="section"
          direction="column"
          width="100%"
          height="100%"
          paddingX="32px"
          paddingY="24px"
          bgcolor="#ffffff"
          borderRadius="8px"
          boxShadow="0 4px 8px 0 #00000019"
          gap="32px"
        >
          <Stack component="article" direction="column" width="100%" gap="28px">
            <Stack
              component="header"
              direction="row"
              alignItems="center"
              width="100%"
              height="25px"
              gap="14px"
            >
              <Typography
                fontSize="18px"
                fontWeight="600"
                lineHeight="normal"
                color="#595958"
              >
                {t("Account team")}
              </Typography>
              <Stack height="1px" width="auto" flexGrow="1" bgcolor="#cacdcd" />
            </Stack>
            <Stack
              component="section"
              direction="row"
              width="100%"
              gap="32px"
              flexWrap="nowrap"
              position="relative"
            >
              {accountNumber === CrmAccount.Internal ? (
                <Member
                  name="Nick Allan"
                  id="nick.allan@controlrisks.com"
                  onClick={() =>
                    handleContactTeamToggle({
                      accountTeamEmailAddress:
                        "MyControlRisks@controlrisks.com",
                      accountTeamFullName: "Nick Allan",
                      accountTeamId: "",
                      crmAccountId: "",
                      crmAccountName: "Control Risks",
                      crmAccountNumber: accountNumber,
                    })
                  }
                />
              ) : (
                <Fragment>
                  {isAccountError || isAccountTeamError ? (
                    <Stack
                      direction="column"
                      alignItems="center"
                      gap="16px"
                      position="absolute"
                      top="0"
                      left="0"
                      width="100%"
                      height="100%"
                      bgcolor="#FFFFFFB3"
                      zIndex="10"
                    >
                      <Typography
                        fontSize="14px"
                        fontWeight="400"
                        color="#595958"
                        lineHeight="1.43"
                        textAlign="center"
                      >
                        <Trans
                          i18nKey="We're sorry, we're having trouble retrieving this right now. Please try refreshing your browser or contact us."
                          defaults="We're sorry, we're having trouble retrieving this right now. Please try refreshing your browser or <ContactLink>contact us</ContactLink>"
                          components={{
                            ContactLink: (
                              <Typography
                                component="span"
                                color="#226CA5"
                                fontSize="0.875rem"
                                sx={{
                                  textDecoration: "underline",
                                  cursor: "pointer",
                                }}
                                onClick={handleOpenSupportModal}
                              >
                                contact us
                              </Typography>
                            ),
                          }}
                        />
                      </Typography>
                      <NewButton
                        variant="outline"
                        color="primary"
                        text={t("Refresh")}
                        onClick={handleRefetchAll}
                        sx={{
                          height: "32px",
                        }}
                      />
                    </Stack>
                  ) : null}
                  <Member
                    loading={isLoadingAccount}
                    name={getAccountQuery.data?.relationshipManager || ""}
                    id={getAccountQuery.data?.relationshipManagerId}
                    onClick={() =>
                      handleContactTeamToggle({
                        accountTeamEmailAddress:
                          getAccountQuery.data?.relationshipManagerEmail!,
                        accountTeamFullName:
                          getAccountQuery.data?.relationshipManager || "",
                        accountTeamId: "",
                        crmAccountId: getAccountQuery.data?.crmAccountId || "",
                        crmAccountName:
                          getAccountQuery.data?.crmAccountName || "",
                        crmAccountNumber: accountNumber,
                      })
                    }
                  />
                  {isLoadingAccountTeam ? (
                    [1, 2, 3].map((_, i) => (
                      <Member key={i} name="" loading={true} />
                    ))
                  ) : getAccounTeamQuery.data &&
                    getAccounTeamQuery.data?.length ? (
                    <Fragment>
                      <Box
                        height="90px"
                        width="1px"
                        bgcolor="#dedede"
                        marginY="auto"
                        marginX="8px"
                      />
                      <Stack flexDirection="row" gap="32px" flexWrap="wrap">
                        {getAccounTeamQuery.data
                          .sort((a, b) => {
                            if (a.accountTeamFullName < b.accountTeamFullName) {
                              return -1
                            }
                            if (a.accountTeamFullName > b.accountTeamFullName) {
                              return 1
                            }
                            return 0
                          })
                          .map((member, i) => (
                            <Member
                              key={i}
                              name={member.accountTeamFullName}
                              id={member.accountTeamId}
                              onClick={() => handleContactTeamToggle(member)}
                            />
                          ))}
                      </Stack>
                    </Fragment>
                  ) : null}
                </Fragment>
              )}
            </Stack>
          </Stack>
          <Stack component="article" direction="column" width="100%" gap="24px">
            <Stack
              component="header"
              direction="row"
              alignItems="center"
              width="100%"
              height="25px"
              gap="14px"
            >
              <Typography
                fontSize="18px"
                fontWeight="600"
                lineHeight="normal"
                color="#595958"
              >
                {t("Company details")}
              </Typography>
              <Stack height="1px" width="auto" flexGrow="1" bgcolor="#cacdcd" />
            </Stack>
            <Stack
              component="section"
              direction="column"
              gap="24px"
              position="relative"
            >
              {isAccountError ? (
                <Stack
                  direction="column"
                  alignItems="center"
                  gap="16px"
                  position="absolute"
                  top="0"
                  left="0"
                  width="100%"
                  height="100%"
                  bgcolor="#FFFFFFB3"
                  zIndex="10"
                >
                  <Typography
                    fontSize="14px"
                    fontWeight="400"
                    color="#595958"
                    lineHeight="1.43"
                    textAlign="center"
                  >
                    <Trans
                      i18nKey="We're sorry, we're having trouble retrieving this right now. Please try refreshing your browser or contact us."
                      defaults="We're sorry, we're having trouble retrieving this right now. Please try refreshing your browser or <ContactLink>contact us</ContactLink>"
                      components={{
                        ContactLink: (
                          <Typography
                            component="span"
                            color="#226CA5"
                            fontSize="0.875rem"
                            sx={{
                              textDecoration: "underline",
                              cursor: "pointer",
                            }}
                            onClick={handleOpenSupportModal}
                          >
                            contact us
                          </Typography>
                        ),
                      }}
                    />
                  </Typography>
                  <NewButton
                    variant="outline"
                    color="primary"
                    text={t("Refresh")}
                    onClick={handleRefetchAccount}
                    sx={{
                      height: "32px",
                    }}
                  />
                </Stack>
              ) : null}
              <Detail
                id="company"
                isLoading={isLoadingAccount}
                title="Company"
                detail={
                  accountNumber === CrmAccount.Internal
                    ? "Control Risks"
                    : getAccountQuery.data?.crmAccountName
                }
                detailProps={{
                  whiteSpace: "nowrap",
                }}
                width="164px"
                truncated
              />
              <Stack direction="row" gap={{xs: "20px", md: "67px"}}>
                <Detail
                  id="streetLine1"
                  isLoading={isLoadingAccount}
                  title="Street line 1"
                  detail={
                    accountNumber === CrmAccount.Internal
                      ? "6th Floor, 33 King William Street"
                      : getAccountQuery.data?.addressline1
                  }
                  truncated
                />
                <Detail
                  id="streetLine2"
                  isLoading={isLoadingAccount}
                  title="Street line 2"
                  detail={getAccountQuery.data?.addressline2}
                  truncated
                />
                <Detail
                  id="streetLine3"
                  isLoading={isLoadingAccount}
                  title="Street line 3"
                  detail={getAccountQuery.data?.addressline3}
                  truncated
                />
                <Detail
                  id="city"
                  isLoading={isLoadingAccount}
                  title="City"
                  detail={
                    accountNumber === CrmAccount.Internal
                      ? "London"
                      : getAccountQuery.data?.city
                  }
                  truncated
                />
              </Stack>
              <Stack direction="row" gap={{xs: "20px", md: "67px"}}>
                <Detail
                  id="zipPostalCode"
                  isLoading={isLoadingAccount}
                  title="ZIP / Postal code"
                  detail={
                    accountNumber === CrmAccount.Internal
                      ? "EC4R 9AT"
                      : getAccountQuery.data?.postalCode
                  }
                  truncated
                />
                <Detail
                  id="country"
                  isLoading={isLoadingAccount}
                  title="Country"
                  detail={
                    accountNumber === CrmAccount.Internal
                      ? "United Kingdom"
                      : getAccountQuery.data?.country
                  }
                  truncated
                />
              </Stack>
            </Stack>
          </Stack>
        </Stack>
      </Stack>
      <ContactModal
        show={showContactTeam}
        handleToggle={() => setShowContactTeam((s) => !s)}
        member={selectedMember!!}
        account={getAccountQuery.data!!}
        accountTeam={getAccounTeamQuery.data!!}
      />
      <SupportModal
        show={showSupportModal}
        handleToggle={handleOpenSupportModal}
        title="New client support request"
      />
    </Fragment>
  )
}

Details.displayName = "Details"
export default Details
